(() => {
  if (window.md) return;
  document.documentElement.classList.add('md-js');

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const RIPPLE_SELECTOR =
    '.md-button, .md-icon-button, .md-fab, .md-chip, .md-card--interactive, .md-list-item--interactive, .md-menu-item, .md-nav-item, .md-tab, .md-segmented__option';

  document.addEventListener('pointerdown', (event) => {
    if (reducedMotion.matches || event.button !== 0) return;
    const target = event.target.closest(RIPPLE_SELECTOR);
    if (!target || target.matches(':disabled, [aria-disabled="true"]')) return;

    let host = target.querySelector(':scope > .md-ripple-host');
    if (!host) {
      host = document.createElement('span');
      host.className = 'md-ripple-host';
      host.setAttribute('aria-hidden', 'true');
      target.prepend(host);
    }

    const rect = target.getBoundingClientRect();
    const size = Math.hypot(rect.width, rect.height) * 2;
    const ripple = document.createElement('span');
    ripple.className = 'md-ripple';
    ripple.style.cssText = `width:${size}px;height:${size}px;left:${event.clientX - rect.left}px;top:${event.clientY - rect.top}px`;
    host.append(ripple);

    const release = () => {
      ripple.classList.add('is-fading');
      setTimeout(() => ripple.remove(), 250);
      window.removeEventListener('pointerup', release);
      window.removeEventListener('pointercancel', release);
    };
    window.addEventListener('pointerup', release);
    window.addEventListener('pointercancel', release);
  });

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[aria-pressed]:is(button, [role="button"])');
    if (!toggle || toggle.matches(':disabled, [aria-disabled="true"]')) return;

    const group = toggle.closest('[data-md-group]');
    if (group?.dataset.mdGroup === 'single') {
      group.querySelectorAll('[aria-pressed]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button === toggle));
      });
    } else {
      toggle.setAttribute('aria-pressed', String(toggle.getAttribute('aria-pressed') !== 'true'));
    }
    toggle.dispatchEvent(new CustomEvent('md:toggle', { bubbles: true, detail: { pressed: toggle.getAttribute('aria-pressed') === 'true' } }));
  });

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-md-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.mdOpen);
      if (dialog?.showModal && !dialog.open) {
        event.preventDefault();
        dialog.showModal();
        opener.setAttribute('aria-expanded', 'true');
        dialog.addEventListener('close', () => opener.setAttribute('aria-expanded', 'false'), { once: true });
      }
      return;
    }

    const closer = event.target.closest('[data-md-close]');
    if (closer) {
      closer.closest('dialog')?.close(closer.dataset.mdClose || '');
      return;
    }

    if (event.target instanceof HTMLDialogElement && event.target.matches('.md-dialog, .md-sheet, .md-nav-drawer, .md-lightbox')) {
      const rect = event.target.getBoundingClientRect();
      const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) event.target.close();
    }
  });

  const supportsAnchor = CSS.supports('position-area: block-end');

  document.addEventListener(
    'toggle',
    (event) => {
      const popover = event.target;
      if (!(popover instanceof HTMLElement) || !popover.matches('.md-menu, .md-tooltip-rich')) return;
      const invoker = document.querySelector(`[popovertarget="${popover.id}"], [data-md-rich-tooltip="${popover.id}"]`);
      const open = event.newState === 'open';
      invoker?.setAttribute('aria-expanded', String(open));

      if (open && invoker && !supportsAnchor) {
        const rect = invoker.getBoundingClientRect();
        const menuRect = popover.getBoundingClientRect();
        const below = rect.bottom + menuRect.height < window.innerHeight;
        popover.style.top = `${below ? rect.bottom : rect.top - menuRect.height}px`;
        popover.style.left = `${Math.min(rect.left, window.innerWidth - menuRect.width - 8)}px`;
      }
    },
    true
  );

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-md-fab-toggle]');
    if (!toggle) return;
    const menu = toggle.closest('.md-fab-menu');
    const open = menu.classList.toggle('is-open');
    toggle.setAttribute('aria-expanded', String(open));
    menu.querySelectorAll('.md-fab-menu__item').forEach((item, index, all) => {
      item.style.setProperty('--md-fab-item-index', index);
      item.style.setProperty('--md-fab-item-count', all.length);
    });
  });

  const selectTab = (tab) => {
    const list = tab.closest('[role="tablist"]');
    list.querySelectorAll('[role="tab"]').forEach((other) => {
      const selected = other === tab;
      other.setAttribute('aria-selected', String(selected));
      other.tabIndex = selected ? 0 : -1;
      const panel = document.getElementById(other.getAttribute('aria-controls'));
      if (panel) panel.hidden = !selected;
    });
  };

  document.addEventListener('click', (event) => {
    const tab = event.target.closest('[role="tab"]');
    if (tab) selectTab(tab);
  });

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-md-lightbox]');
    if (!opener) return;
    const template = document.getElementById(opener.dataset.mdLightbox);
    if (!template) return;
    event.preventDefault();
    let dialog = document.getElementById('MdLightbox');
    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.id = 'MdLightbox';
      dialog.className = 'md-lightbox';
      dialog.innerHTML = '<button type="button" class="md-icon-button md-lightbox__close" data-md-close aria-label="Close"><span class="md-icon" aria-hidden="true">close</span></button><div class="md-lightbox__body"></div>';
      dialog.addEventListener('close', () => dialog.querySelector('.md-lightbox__body').replaceChildren());
      document.body.append(dialog);
    }
    dialog.setAttribute('aria-label', opener.getAttribute('aria-label') || 'Video');
    dialog.querySelector('.md-lightbox__body').replaceChildren(template.content.cloneNode(true));
    dialog.showModal();
    dialog.querySelector('video')?.play().catch(() => {});
  });

  const galleryItems = (gallery) =>
    [...gallery.querySelectorAll('[data-md-gallery-item]')].filter((item) => !item.closest('[hidden]'));

  const openGallery = (gallery, startIndex) => {
    let dialog = document.getElementById('MdGallery');
    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.id = 'MdGallery';
      dialog.className = 'md-lightbox md-lightbox--gallery';
      dialog.setAttribute('aria-label', 'Gallery');
      dialog.innerHTML = `
        <button type="button" class="md-icon-button md-lightbox__close" data-md-close aria-label="Close"><span class="md-icon" aria-hidden="true">close</span></button>
        <figure class="md-lightbox__figure">
          <img class="md-lightbox__image" alt="">
          <figcaption class="md-lightbox__caption">
            <span class="md-lightbox__text md-typescale-body-medium"></span>
            <span class="md-lightbox__count md-typescale-label-medium"></span>
            <a class="md-lightbox__link md-typescale-label-large" hidden></a>
          </figcaption>
        </figure>
        <button type="button" class="md-icon-button md-icon-button--m md-lightbox__nav md-lightbox__nav--prev" data-md-gallery-step="-1" aria-label="Previous"><span class="md-icon" aria-hidden="true">chevron_left</span></button>
        <button type="button" class="md-icon-button md-icon-button--m md-lightbox__nav md-lightbox__nav--next" data-md-gallery-step="1" aria-label="Next"><span class="md-icon" aria-hidden="true">chevron_right</span></button>`;
      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') dialog.mdStep?.(1);
        if (event.key === 'ArrowLeft') dialog.mdStep?.(-1);
      });
      dialog.addEventListener('click', (event) => {
        const step = event.target.closest('[data-md-gallery-step]');
        if (step) dialog.mdStep?.(Number(step.dataset.mdGalleryStep));
      });
      document.body.append(dialog);
    }

    const items = galleryItems(gallery);
    let index = Math.max(0, startIndex);
    const img = dialog.querySelector('.md-lightbox__image');
    const text = dialog.querySelector('.md-lightbox__text');
    const count = dialog.querySelector('.md-lightbox__count');
    const link = dialog.querySelector('.md-lightbox__link');

    const show = (i) => {
      index = (i + items.length) % items.length;
      const item = items[index];
      img.src = item.dataset.src;
      img.alt = item.dataset.alt || '';
      text.textContent = item.dataset.caption || '';
      count.textContent = `${index + 1} / ${items.length}`;
      link.hidden = !item.dataset.href;
      if (item.dataset.href) {
        link.href = item.dataset.href;
        link.textContent = item.dataset.hrefLabel || 'View case study';
      }
      const next = items[(index + 1) % items.length];
      if (next) new Image().src = next.dataset.src;
    };

    dialog.mdStep = (delta) => show(index + delta);
    dialog.querySelectorAll('.md-lightbox__nav').forEach((nav) => (nav.hidden = items.length < 2));
    show(index);
    if (!dialog.open) dialog.showModal();
  };

  document.addEventListener('click', (event) => {
    const item = event.target.closest('[data-md-gallery-item]');
    if (!item) return;
    const gallery = item.closest('[data-md-gallery]');
    if (!gallery) return;
    event.preventDefault();
    openGallery(gallery, galleryItems(gallery).indexOf(item));
  });

  document.addEventListener('click', (event) => {
    const tab = event.target.closest('[data-md-gallery-filter]');
    if (!tab) return;
    const gallery = tab.closest('[data-md-gallery]');
    const value = tab.dataset.mdGalleryFilter;
    gallery.querySelectorAll('[data-md-gallery-filter]').forEach((other) => other.setAttribute('aria-pressed', String(other === tab)));
    gallery.querySelectorAll('[data-md-gallery-group]').forEach((cell) => {
      cell.hidden = value !== 'all' && !cell.dataset.mdGalleryGroup.split('|').includes(value);
    });
  });

  document.addEventListener('shopify:block:select', (event) => {
    const tab = document.querySelector(`[data-block-tab="${event.detail.blockId}"]`);
    if (tab) selectTab(tab);
  });

  document.addEventListener('keydown', (event) => {
    const tab = event.target.closest?.('[role="tab"]');
    if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    const tabs = [...tab.closest('[role="tablist"]').querySelectorAll('[role="tab"]')];
    const index = tabs.indexOf(tab);
    const next = {
      ArrowLeft: tabs[(index - 1 + tabs.length) % tabs.length],
      ArrowRight: tabs[(index + 1) % tabs.length],
      Home: tabs[0],
      End: tabs[tabs.length - 1],
    }[event.key];
    event.preventDefault();
    next.focus();
    selectTab(next);
  });

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-md-share]');
    if (!button) return;
    const url = button.dataset.mdShare || window.location.href;
    const title = button.dataset.mdShareTitle || document.title;
    if (navigator.share) {
      try {
        await navigator.share({ url, title });
      } catch (error) {
      }
      return;
    }
    await navigator.clipboard?.writeText(url);
    snackbar(button.dataset.mdShareCopied || 'Link copied');
  });

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-md-copy]');
    if (!button) return;
    await navigator.clipboard?.writeText(button.dataset.mdCopy);
    snackbar(button.dataset.mdCopied || 'Copied');
  });

  const darkQuery = window.matchMedia('(prefers-color-scheme: dark)');
  const isDark = () => {
    const mode = document.documentElement.dataset.mdMode;
    return mode === 'dark' || (mode === 'system' && darkQuery.matches);
  };
  const syncThemeToggles = () => {
    document.querySelectorAll('[data-md-theme-toggle]').forEach((toggle) => {
      toggle.checked = isDark();
    });
  };

  document.addEventListener('change', (event) => {
    const toggle = event.target.closest?.('[data-md-theme-toggle]');
    if (!toggle) return;
    const root = document.documentElement;
    if (root.dataset.mdLockedMode) {
      toggle.checked = isDark();
      return;
    }
    const mode = toggle.checked ? 'dark' : 'light';
    const apply = () => {
      root.dataset.mdMode = mode;
      syncThemeToggles();
    };
    try {
      localStorage.setItem('md-mode', mode);
    } catch (error) {}

    if (!document.startViewTransition || reducedMotion.matches) {
      apply();
      return;
    }

    const rect = toggle.getBoundingClientRect();
    const x = rect.left + rect.width / 2;
    const y = rect.top + rect.height / 2;
    const radius = Math.hypot(Math.max(x, innerWidth - x), Math.max(y, innerHeight - y));
    root.classList.add('md-mode-switching');
    const transition = document.startViewTransition(apply);
    transition.ready.then(() => {
      root.animate(
        { clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${radius}px at ${x}px ${y}px)`] },
        { duration: 600, easing: 'cubic-bezier(0.2, 0, 0, 1)', pseudoElement: '::view-transition-new(root)' }
      );
    });
    transition.finished.finally(() => root.classList.remove('md-mode-switching'));
  });
  darkQuery.addEventListener('change', syncThemeToggles);

  let tooltipTimer;
  const richTooltipFor = (target) => {
    const trigger = target.closest?.('[data-md-rich-tooltip]');
    return trigger ? [trigger, document.getElementById(trigger.dataset.mdRichTooltip)] : [];
  };
  const showRich = (event) => {
    const [trigger, tip] = richTooltipFor(event.target);
    if (!tip) return;
    clearTimeout(tooltipTimer);
    tooltipTimer = setTimeout(() => {
      if (!tip.matches(':popover-open')) tip.showPopover({ source: trigger });
    }, event.type === 'focusin' ? 0 : 500);
  };
  const hideRich = (event) => {
    const [, tip] = richTooltipFor(event.target);
    if (!tip) return;
    clearTimeout(tooltipTimer);
    tooltipTimer = setTimeout(() => {
      if (tip.matches(':popover-open') && !tip.matches(':hover')) tip.hidePopover();
    }, 300);
  };
  document.addEventListener('pointerover', showRich);
  document.addEventListener('pointerout', hideRich);
  document.addEventListener('focusin', showRich);
  document.addEventListener('focusout', hideRich);

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-md-carousel-prev], [data-md-carousel-next]');
    if (!button) return;
    const id = button.dataset.mdCarouselPrev || button.dataset.mdCarouselNext;
    const carousel = document.getElementById(id);
    if (!carousel) return;
    const pad = parseFloat(getComputedStyle(carousel).scrollPaddingInlineStart) || 0;
    const origin = carousel.getBoundingClientRect().left - carousel.scrollLeft + pad;
    const max = carousel.scrollWidth - carousel.clientWidth;
    const stops = [...carousel.querySelectorAll('.md-carousel__item')].map((item) => Math.min(max, item.getBoundingClientRect().left - origin));
    const current = carousel.scrollLeft;
    const target = button.hasAttribute('data-md-carousel-prev')
      ? [...stops].reverse().find((stop) => stop < current - 2) ?? 0
      : stops.find((stop) => stop > current + 2) ?? max;
    carousel.scrollTo({ left: target, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  });

  const syncSlider = (slider) => {
    const min = Number(slider.min || 0);
    const max = Number(slider.max || 100);
    slider.style.setProperty('--_value', `${((slider.value - min) / (max - min)) * 100}%`);
  };
  document.addEventListener('input', (event) => {
    if (event.target.matches?.('.md-slider')) syncSlider(event.target);
  });

  let snackbarTimer;
  const snackbar = (message, { action, onAction, duration = 4000, closable = false } = {}) => {
    document.querySelector('.md-snackbar')?.remove();
    const bar = document.createElement('div');
    bar.className = 'md-snackbar';
    bar.popover = 'manual';
    bar.setAttribute('role', 'status');

    const text = document.createElement('span');
    text.className = 'md-snackbar__text';
    text.textContent = message;
    bar.append(text);

    const dismiss = () => {
      clearTimeout(snackbarTimer);
      bar.hidePopover();
      setTimeout(() => bar.remove(), 300);
    };

    if (action) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'md-button md-button--text';
      button.textContent = action;
      button.addEventListener('click', () => {
        onAction?.();
        dismiss();
      });
      bar.append(button);
    }

    if (closable) {
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'md-icon-button';
      close.setAttribute('aria-label', 'Close');
      close.innerHTML = '<span class="md-icon" aria-hidden="true">close</span>';
      close.addEventListener('click', dismiss);
      bar.append(close);
    }

    document.body.append(bar);
    bar.showPopover();
    snackbarTimer = setTimeout(dismiss, duration);
    return dismiss;
  };

  let lenis;
  const initLenis = () => {
    const root = document.documentElement;
    if (lenis || !window.Lenis || root.dataset.mdSmoothScroll !== 'true') return;
    if (reducedMotion.matches || window.Shopify?.designMode) return;
    const headerOffset = parseFloat(getComputedStyle(root).getPropertyValue('--md-header-height')) || 0;
    lenis = new window.Lenis({
      autoRaf: true,
      anchors: { offset: -(headerOffset + 16) },
      prevent: (node) => node.closest?.('dialog, [popover], .md-carousel, .md-menu, .ovxs-menu-overlay, [data-lenis-prevent]'),
    });
    document.addEventListener(
      'toggle',
      () => (document.querySelector('dialog:modal') ? lenis.stop() : lenis.start()),
      true
    );
    document.addEventListener('close', () => lenis.start(), true);
    window.md.lenis = lenis;
  };

  const splitText = (el) => {
    if (el.dataset.mdSplitDone) return;
    el.dataset.mdSplitDone = 'true';
    let index = 0;
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach((node) => {
      const fragment = document.createDocumentFragment();
      node.textContent.split(/(\s+)/).forEach((part) => {
        if (!part) return;
        if (/^\s+$/.test(part)) {
          fragment.append(part);
          return;
        }
        const word = document.createElement('span');
        word.className = 'md-split-word';
        const inner = document.createElement('span');
        inner.className = 'md-split-inner';
        inner.style.setProperty('--md-split-index', index++);
        inner.textContent = part;
        word.append(inner);
        fragment.append(word);
      });
      node.replaceWith(fragment);
    });
  };

  let mosaicsBound = false;
  const initMosaics = () => {
    if (!document.querySelector('[data-md-mosaic]')) return;
    const update = () => {
      document.querySelectorAll('[data-md-mosaic]').forEach((mosaic) => {
        const zoom = Number(mosaic.dataset.mdMosaicZoom) || 3;
        if (reducedMotion.matches) {
          mosaic.style.setProperty('--md-mosaic-scale', 1);
          mosaic.toggleAttribute('data-md-mosaic-done', true);
          return;
        }
        const rect = mosaic.getBoundingClientRect();
        const distance = Math.max(1, rect.height - innerHeight);
        const progress = Math.min(1, Math.max(0, -rect.top / distance));
        const eased = 1 - Math.pow(1 - progress, 3);
        mosaic.style.setProperty('--md-mosaic-scale', (zoom + (1 - zoom) * eased).toFixed(4));
        mosaic.toggleAttribute('data-md-mosaic-done', progress > 0.85);
      });
    };
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        update();
      });
    };
    update();
    if (!mosaicsBound) {
      mosaicsBound = true;
      addEventListener('scroll', onScroll, { passive: true });
      addEventListener('resize', onScroll);
    }
  };

  const init = () => {
    initLenis();
    initMosaics();
    if (document.documentElement.dataset.mdSplit === 'true' && !reducedMotion.matches && !window.Shopify?.designMode) {
      document.querySelectorAll('[data-md-split-text]').forEach((el) => {
        splitText(el);
        if (!el.hasAttribute('data-md-reveal')) el.setAttribute('data-md-reveal', 'split');
      });
    }
    document.querySelectorAll('.md-slider').forEach(syncSlider);
    syncThemeToggles();

    const appBars = document.querySelectorAll('.md-top-app-bar');
    if (appBars.length) {
      const onScroll = () => appBars.forEach((bar) => bar.toggleAttribute('data-scrolled', window.scrollY > 4));
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
    }

    const revealables = document.querySelectorAll('[data-md-reveal]');
    if (!revealables.length) return;
    if (document.documentElement.classList.contains('md-pt-arriving')) {
      document.addEventListener('md:pt:done', () => observeReveals(revealables), { once: true });
      return;
    }
    const preloader = document.querySelector('[data-md-preloader-el]');
    if (preloader && document.documentElement.classList.contains('md-preloading')) {
      document.addEventListener('md:preloader:done', () => observeReveals(revealables), { once: true });
      return;
    }
    observeReveals(revealables);
  };

  const observeReveals = (revealables) => {
    if (!('IntersectionObserver' in window) || reducedMotion.matches) {
      revealables.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px' }
    );
    revealables.forEach((el) => observer.observe(el));
  };

  window.md = { snackbar, selectTab };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);

})();
